import { useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { IonReorder, IonReorderGroup, ItemReorderEventDetail } from '@ionic/react';
import { useCallback, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import * as yup from 'yup';

import ActionIconButton from '@components/buttons/ActionIconButton';
import TertiaryButton from '@components/buttons/TertiaryButton';
import BaseErrors from '@components/fields/BaseErrors';
import FormButtons from '@components/fields/FormButtons';
import Input from '@components/fields/Input';
import Select from '@components/fields/Select';
import Form from '@components/forms/Form';
import styles from '@components/forms/ResourceFeatureTemplateForm.module.css';
import {
  ResourceFeatureKindEnum,
  ResourceFeatureTemplateFragment as ResourceFeatureTemplate,
  ResourceFeatureTemplateCreateDocument,
  ResourceFeatureTemplateCreateMutation,
  ResourceFeatureTemplateCreateMutationVariables,
  ResourceFeatureTemplateUpdateDocument,
  ResourceFeatureTemplateUpdateMutation,
  ResourceFeatureTemplateUpdateMutationVariables
} from '@typing/Generated';
import { handleMutationResponse } from '@utils/formUtils';
import { sortByPosition } from '@utils/modelUtils';

type ResourceFeature = {
  featureId: string | null;
  kind: ResourceFeatureKindEnum;
  name: string;
};

type FormData = {
  resourceFeatures: ResourceFeature[];
  title: string;
};

type Props = {
  autofocus: boolean;
  onCancel: () => void;
  onError: () => void;
  onSuccess: () => void;
  resourceFeatureTemplate?: ResourceFeatureTemplate;
};

const ResourceFeatureTemplateForm = ({ autofocus, onCancel, onError, onSuccess, resourceFeatureTemplate }: Props) => {
  const existingFeatures: ResourceFeature[] =
    sortByPosition(resourceFeatureTemplate?.resourceFeatures ?? []).map(feature => ({
      featureId: feature.id,
      kind: feature.kind,
      name: feature.name
    })) ?? [];

  const initialValues: FormData = {
    resourceFeatures: existingFeatures,
    title: resourceFeatureTemplate?.title ?? ''
  };

  const intl = useIntl();

  const [baseErrors, setBaseErrors] = useState<string[]>([]);

  const [createResourceFeatureTemplate, { client }] = useMutation<
    ResourceFeatureTemplateCreateMutation,
    ResourceFeatureTemplateCreateMutationVariables
  >(ResourceFeatureTemplateCreateDocument, {
    update: () => {
      client.resetStore();
    }
  });

  const [updateResourceFeatureTemplate] = useMutation<
    ResourceFeatureTemplateUpdateMutation,
    ResourceFeatureTemplateUpdateMutationVariables
  >(ResourceFeatureTemplateUpdateDocument);

  const schema = yup
    .object()
    .shape({
      resourceFeatures: yup
        .array()
        .of(
          yup
            .object()
            .shape({
              kind: yup.string().required(intl.formatMessage({ id: 'errors.resourceFeature.kind.blank' })),
              name: yup.string().required(intl.formatMessage({ id: 'errors.resourceFeature.name.blank' }))
            })
            .required()
        )
        .min(1),
      title: yup.string().required(intl.formatMessage({ id: 'errors.resourceFeatureTemplate.title.blank' }))
    })
    .required();

  const form = useForm<FormData>({
    defaultValues: initialValues,
    mode: 'onChange',
    resolver: yupResolver(schema)
  });

  const {
    append: resourceFeatureAppend,
    fields: resourceFeatureFields,
    move: resourceFeatureMove,
    remove: resourceFeatureRemove
  } = useFieldArray<FormData>({
    control: form.control,
    name: 'resourceFeatures'
  });

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);

    const mappedFeatures = form.getValues().resourceFeatures.map(rf => ({
      id: rf.featureId,
      kind: rf.kind,
      name: rf.name
    }));

    if (resourceFeatureTemplate?.id) {
      return updateResourceFeatureTemplate({
        variables: {
          ...form.getValues(),
          id: resourceFeatureTemplate.id,
          resourceFeatures: mappedFeatures
        }
      }).then(response => {
        handleMutationResponse<FormData>({
          data: response.data?.resourceFeatureTemplateUpdate,
          intl,
          onError,
          onSuccess: () => {
            if (onSuccess) {
              onSuccess();
            }
          },
          setBaseErrors,
          setError: form.setError,
          txBase: 'errors.resourceFeatureTemplate'
        });
      });
    }
    return createResourceFeatureTemplate({
      variables: {
        ...form.getValues(),
        resourceFeatures: mappedFeatures
      }
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response.data?.resourceFeatureTemplateCreate,
        intl,
        onSuccess,
        setBaseErrors,
        setError: form.setError,
        txBase: 'errors.resourceFeatureTemplate'
      });
    });
  }, [
    resourceFeatureTemplate?.id,
    createResourceFeatureTemplate,
    form,
    updateResourceFeatureTemplate,
    intl,
    onError,
    onSuccess
  ]);

  const kindOptions = [
    {
      label: intl.formatMessage({ id: 'forms.resourceFeature.kinds.boolean' }),
      value: ResourceFeatureKindEnum.BOOLEAN
    },
    {
      label: intl.formatMessage({ id: 'forms.resourceFeature.kinds.text' }),
      value: ResourceFeatureKindEnum.TEXT
    }
  ];

  const reorderFeatures = useCallback(
    (event: CustomEvent<ItemReorderEventDetail>) => {
      resourceFeatureMove(event.detail.from, event.detail.to);
      event.detail.complete();
    },
    [resourceFeatureMove]
  );

  return (
    <>
      <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
        <BaseErrors errors={baseErrors} />
        <Input<FormData>
          autofocus={autofocus}
          control={form.control}
          error={form.formState.errors.title}
          label={intl.formatMessage({ id: 'forms.goalTemplate.titleLabel' })}
          name="title"
          required
        />

        <IonReorderGroup disabled={false} onIonItemReorder={reorderFeatures}>
          {resourceFeatureFields.map((field, index) => (
            <div className={styles.resourceFeatureRow} key={field.id}>
              <div className={styles.resourceFeatureRowReorder}>
                <IonReorder slot="start" />
              </div>
              <div className={styles.resourceFeatureRowControl}>
                <Select<FormData>
                  control={form.control}
                  error={form.formState.errors.resourceFeatures?.[index]?.kind}
                  label={intl.formatMessage({ id: 'forms.resourceFeature.kindLabel' })}
                  name={`resourceFeatures.${index}.kind`}
                  options={kindOptions}
                  required
                />
              </div>
              <div className={styles.resourceFeatureRowControl}>
                <div className="ion-margin-start">
                  <Input<FormData>
                    control={form.control}
                    error={form.formState.errors.resourceFeatures?.[index]?.name}
                    label={intl.formatMessage({ id: 'forms.resourceFeature.nameLabel' })}
                    name={`resourceFeatures.${index}.name`}
                    required
                  />
                </div>
              </div>
              <div>
                <div className={`ion-margin-start ${styles.deleteIcon}`}>
                  <ActionIconButton
                    ariaLabelKey="accessibility.ariaLabels.delete"
                    icon="times"
                    onClick={() => resourceFeatureRemove(index)}
                  />
                </div>
              </div>
            </div>
          ))}
        </IonReorderGroup>
        <TertiaryButton
          icon="add"
          onClick={() => resourceFeatureAppend({ featureId: null, kind: ResourceFeatureKindEnum.TEXT, name: '' })}
          textKey="forms.resourceFeature.addResourceFeature"
        />

        <FormButtons
          hookForm={form}
          onCancel={onCancel}
          saveKey={resourceFeatureTemplate?.id ? 'dictionary.saveChanges' : 'models.resourceFeatureTemplate.create'}
        />
      </Form>
    </>
  );
};

export default ResourceFeatureTemplateForm;
